import { useEffect, useMemo, useState } from "react";
import { Check, AlertCircle, Loader2, Mail } from "lucide-react";
import { STATES, STATE_BY_CODE } from "@/config/states";
import { joinWaitlist } from "@/lib/waitlist";
import { useEligibility } from "@/hooks/use-eligibility";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Status =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "success"; duplicate: boolean }
  | { kind: "error"; message: string };

type Tone = "light" | "dark";

export type WaitlistInlineProps = {
  /** Force a specific state (e.g. on a state landing page). When omitted, the visitor can pick or change their state inline. */
  forceState?: string;
  /** Where this form lives — included in analytics for placement-level conversion tracking. */
  placement: string;
  /** Visual treatment. `dark` is for sage/primary backgrounds. */
  tone?: Tone;
  /** Optional headline shown above the field. */
  label?: string;
  /** Compact = single line, no surrounding card. Used in footer. */
  compact?: boolean;
  /** Always show the state picker (even if a state is already saved). Defaults to false. */
  showStatePicker?: boolean;
  className?: string;
};

export const WaitlistInline = ({
  forceState,
  placement,
  tone = "light",
  label,
  compact = false,
  showStatePicker = false,
  className = "",
}: WaitlistInlineProps) => {
  const { code: savedCode, setCode: setSavedCode } = useEligibility();
  const [pickerCode, setPickerCode] = useState<string>(savedCode || "");
  // Keep picker in sync if the saved state changes elsewhere on the page.
  useEffect(() => {
    if (savedCode && !pickerCode) setPickerCode(savedCode);
  }, [savedCode, pickerCode]);

  const showPicker = !forceState && (showStatePicker || !savedCode);
  const code = forceState || (showPicker ? pickerCode : savedCode);
  const state = code ? STATE_BY_CODE[code] : undefined;

  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const emailValid = EMAIL_RE.test(email.trim());
  const showInlineError = touched && email.length > 0 && !emailValid;
  const canSubmit = !!state && emailValid && status.kind !== "loading";

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!state || !emailValid) return;
    setStatus({ kind: "loading" });
    if (showPicker && pickerCode && pickerCode !== savedCode) setSavedCode(pickerCode, `waitlist_inline:${placement}`);
    const res = await joinWaitlist({ email, state: state.code, placement } as { email: string; state: string; placement?: string });
    if (res.ok === true) setStatus({ kind: "success", duplicate: res.duplicate });
    else setStatus({ kind: "error", message: res.error });
  };

  const inputBase = useMemo(() =>
    tone === "dark"
      ? "bg-primary-foreground/10 border-primary-foreground/25 text-primary-foreground placeholder:text-primary-foreground/50"
      : "bg-background border-border text-foreground placeholder:text-foreground/65",
  [tone]);

  const subtleText = tone === "dark" ? "text-primary-foreground/70" : "text-foreground/70";
  const errorText = tone === "dark" ? "text-destructive-foreground" : "text-destructive";

  if (status.kind === "success") {
    const titleTone = tone === "dark" ? "text-primary-foreground" : "text-foreground";
    const accentTone = tone === "dark" ? "text-primary-foreground" : "text-accent";
    const ringTone = tone === "dark" ? "bg-primary-foreground/15" : "bg-accent/12";
    const emailTo = `mailto:${email.trim()}`;
    return (
      <div
        role="status"
        aria-live="polite"
        className={`text-left ${className}`}
      >
        <div className={`inline-flex items-center justify-center size-10 rounded-full ${ringTone} ${accentTone}`}>
          <Check className="size-5" strokeWidth={2.5} />
        </div>
        <p className={`mt-3 font-display text-xl leading-tight ${titleTone}`}>
          {status.duplicate
            ? `Already on the list for ${state?.name}.`
            : `You're on the list for ${state?.name}.`}
        </p>
        <p className={`mt-2 text-sm ${subtleText}`}>
          {status.duplicate
            ? "We've got you. Watch your inbox — we'll email the moment we go live."
            : "Check your inbox for a quick confirmation. We'll email you the moment your state opens up."}
        </p>
        <a
          href={emailTo}
          className={`mt-4 inline-flex items-center gap-2 text-sm font-medium underline-offset-4 hover:underline ${accentTone}`}
        >
          <Mail className="size-4" /> Open my inbox
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className={`${className}`}>
      {label && (
        <p className={`text-[11px] uppercase tracking-[0.22em] font-medium mb-2.5 ${tone === "dark" ? "text-primary-foreground/65" : "text-foreground/70"}`}>
          {label}
        </p>
      )}
      <div className={`flex flex-col ${compact ? "sm:flex-row" : "sm:flex-row"} gap-2.5`}>
        {showPicker && (
          <div className="relative sm:w-44">
            <select
              value={pickerCode}
              onChange={(e) => setPickerCode(e.target.value)}
              aria-label="Select your state"
              className={`w-full appearance-none rounded-full border px-4 py-3 text-sm pr-9 focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 ${inputBase}`}
            >
              <option value="">Your state</option>
              {STATES.map((s) => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
            <span aria-hidden className={`pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs ${tone === "dark" ? "text-primary-foreground/50" : "text-foreground/40"}`}>▾</span>
          </div>
        )}
        <div className="flex-1">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (status.kind === "error") setStatus({ kind: "idle" });
            }}
            onBlur={() => setTouched(true)}
            placeholder="you@email.com"
            aria-invalid={showInlineError}
            aria-describedby={showInlineError ? `wl-${placement}-err` : undefined}
            disabled={status.kind === "loading"}
            className={`w-full rounded-full border px-4 py-3 text-sm focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 disabled:opacity-60 ${
              showInlineError ? "border-destructive" : ""
            } ${inputBase}`}
          />
        </div>
        <button
          type="submit"
          disabled={!canSubmit}
          className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition disabled:opacity-60 disabled:cursor-not-allowed ${
            tone === "dark"
              ? "bg-background text-foreground hover:opacity-90"
              : "bg-primary text-primary-foreground hover:opacity-90"
          }`}
        >
          {status.kind === "loading" ? (
            <><Loader2 className="size-4 animate-spin" aria-hidden /> Saving…</>
          ) : (
            "Notify me"
          )}
        </button>
      </div>
      {showInlineError && (
        <p id={`wl-${placement}-err`} className={`mt-2 text-xs ${errorText}`}>
          Please enter a valid email address.
        </p>
      )}
      {status.kind === "error" && (
        <p role="alert" className={`mt-2 inline-flex items-start gap-1.5 text-xs ${errorText}`}>
          <AlertCircle className="size-3.5 mt-0.5 shrink-0" />
          {status.message}
        </p>
      )}
    </form>
  );
};
